// ─── Event lifecycle phases ──────────────────────────────────────────────────
//
// Single source of truth for the 5-phase event workflow. Both the staff Kanban
// (components/staff/panels/EventsPanel.tsx) and the per-event phase indicator
// (components/meetings/MeetingTimeline.tsx) read their labels and hover help
// text from here so the two never drift apart.
//
// Phase numbers match the workflow stages: an event sits in "Phase N Completed"
// once the work for stage N is done and it is waiting on the next stage. e.g. a
// freshly submitted event (volunteer finished the form + content) is "Phase 1
// Completed" — the first bucket on the staff board.

export type PhaseKey = "phase1" | "phase2" | "phase3" | "phase4" | "phase5";

export interface PhaseInfo {
    key: PhaseKey;
    number: number;
    completedLabel: string; // "Phase 1 Completed" — used on the Kanban columns
    stageLabel: string; // workflow stage name — matches MeetingTimeline steps
    role: string; // who owns this stage
    subtitle: string; // short one-liner under the Kanban column heading
    helpItems: string[]; // bullet points shown on hover
}

export const PHASES: PhaseInfo[] = [
    {
        key: "phase1",
        number: 1,
        completedLabel: "Phase 1 Completed",
        stageLabel: "Creation",
        role: "Volunteer",
        subtitle: "Submitted — awaiting staff",
        helpItems: [
            "Volunteer filled in the event form (title, date, format, location, speakers).",
            "Reviewed the generated content: description, poster, speaker bios, and agenda.",
            "Approved the content and clicked “Submit and Send to Staff.”",
            "Now waiting for staff to publish & promote (Phase 2).",
        ],
    },
    {
        key: "phase2",
        number: 2,
        completedLabel: "Phase 2 Completed",
        stageLabel: "Publication & Promotion",
        role: "Staff",
        subtitle: "Approved — upcoming",
        helpItems: [
            "Staff reviewed and approved the communication drafts (member, speaker, and volunteer emails + LinkedIn).",
            "Event published to the website (permalink live) and promoted on LinkedIn.",
            "Event is approved and upcoming; automated registration tracking begins.",
        ],
    },
    {
        key: "phase3",
        number: 3,
        completedLabel: "Phase 3 Completed",
        stageLabel: "Registration Tracking",
        role: "Automated",
        subtitle: "Live now",
        helpItems: [
            "Registrations sync automatically from cmtassociation.org so you can see who is coming.",
            "The event has reached its start time and is happening now.",
            "Volunteers can capture live session notes and shared media.",
        ],
    },
    {
        key: "phase4",
        number: 4,
        completedLabel: "Phase 4 Completed",
        stageLabel: "Day of the Event",
        role: "Volunteer",
        subtitle: "Ended — awaiting debrief",
        helpItems: [
            "The event has taken place and its end time has passed.",
            "Live session notes and shared media were captured during the event.",
            "Now waiting for volunteers to submit the post-event debrief (Phase 5).",
        ],
    },
    {
        key: "phase5",
        number: 5,
        completedLabel: "Phase 5 Completed",
        stageLabel: "Post-Event Debrief",
        role: "Volunteer",
        subtitle: "Concluded",
        helpItems: [
            "Volunteers submitted their debrief responses.",
            "The “Technically Speaking” report draft was finalized.",
            "The event is fully concluded — nothing further is required.",
        ],
    },
];

export const PHASE_BY_KEY: Record<PhaseKey, PhaseInfo> = PHASES.reduce(
    (acc, p) => {
        acc[p.key] = p;
        return acc;
    },
    {} as Record<PhaseKey, PhaseInfo>,
);

export const PHASE_BY_NUMBER: Record<number, PhaseInfo> = PHASES.reduce(
    (acc, p) => {
        acc[p.number] = p;
        return acc;
    },
    {} as Record<number, PhaseInfo>,
);
